(ns manage.resource.schema
  (:require [common.url-template :as url-tpl]
            [re-frame.core :as rf]))

;; Handlers and subscriptions for retrieving the list of schemas with their
;; meta-data only

(rf/reg-event-fx :resource.schema/get-schemas
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)]
  (fn [{:keys [url-context] :as cofx} _]
    {:fx [[:http
           {:method :get
            :uri (url-tpl/render :api/schemas url-context)
            :headers {"Authorization" (:resource.user/token cofx)}
            :on-success [:resource.schema/get-schemas-success]
            :on-failure [:resource.schema/get-schemas-failure]}]]}))

(rf/reg-event-db :resource.schema/get-schemas-success
  [(rf/path :resource.schema/schemas)]
  (fn [_ [_ schemas]]
    schemas))

(rf/reg-event-db :resource.schema/get-schemas-failure
  [(rf/path :resource.schema/schemas)]
  (fn [_ _]
    nil))

(rf/reg-sub :resource.schema/schemas
  (fn [db _]
    (:resource.schema/schemas db)))

(rf/reg-sub :resource.schema/meta-by-id
  :<- [:resource.schema/schemas]
  (fn [schemas [_ schema-id]]
    (some (fn [s] (when (= (:id s) schema-id)
                    s)) schemas)))

;; Handlers and subscriptions for retrieving the data-output structure of a
;; schema. This includes all data except for enumerations, sets, and options.

(rf/reg-event-fx :resource.schema/get-schema-output-structure
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)]
  (fn [{:keys [url-context] :as cofx} [_ schema-id param]]
    {:fx [[:http
           {:method :post
            :uri (url-tpl/render :api/schemas-output-structure url-context)
            :params {:schema-urn schema-id :param param}
            :headers {"Authorization" (:resource.user/token cofx)}
            :on-success [:resource.schema/get-schema-output-structure-success schema-id param]
            :on-failure [:resource.schema/get-schema-output-structure-failure schema-id param]}]]}))

(rf/reg-event-db :resource.schema/get-schema-output-structure-success
  [(rf/path :resource.schema/output-structure)]
  (fn [schema->output-structure [_ schema-id param output]]
    (assoc-in schema->output-structure [schema-id param] output)))

(rf/reg-event-db :resource.schema/get-schema-output-structure-failure
  [(rf/path :resource.schema/output-structure)]
  (fn [schema->output-structure [_ schema-id param _error]]
    (update-in schema->output-structure [schema-id] dissoc param)))

(rf/reg-sub :resource.schema/output-structures
  (fn [db _]
    (:resource.schema/output-structure db)))

(rf/reg-sub :resource.schema/output-structure
  :<- [:resource.schema/output-structures]
  (fn [output-structures [_ schema-id param]]
    (get-in output-structures [schema-id param])))

;; Handlers and subscriptions for retrieving the data-output of a schema. This
;; complements the get-schema-output-structure handler and only includes
;; enumerations, sets, and options.

(rf/reg-event-fx :resource.schema/get-schema-output-data
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)]
  (fn [{:keys [url-context] :as cofx} [_ schema-id param]]
    {:fx [[:http
           {:method :post
            :uri (url-tpl/render :api/schemas-output-data url-context)
            :params {:schema-urn schema-id :param param}
            :headers {"Authorization" (:resource.user/token cofx)}
            :on-success [:resource.schema/get-schema-output-data-success schema-id param]
            :on-failure [:resource.schema/get-schema-output-data-failure schema-id param]}]]}))

(rf/reg-event-db :resource.schema/get-schema-output-data-success
  [(rf/path :resource.schema/output-data)]
  (fn [schema->output-data [_ schema-id param output]]
    (assoc-in schema->output-data [schema-id param] output)))

(rf/reg-event-db :resource.schema/get-schema-output-data-failure
  [(rf/path :resource.schema/output-data)]
  (fn [schema->output-data [_ schema-id param _error]]
    (update-in schema->output-data [schema-id] dissoc param)))

(rf/reg-sub :resource.schema/output-datas ; I know, data is already plural :)
  (fn [db _]
    (:resource.schema/output-data db)))

(rf/reg-sub :resource.schema/output-data
  :<- [:resource.schema/output-datas]
  (fn [output-datas [_ schema-id param]]
    (get-in output-datas [schema-id param])))

(rf/reg-sub :resource.schema/output
  (fn [[_ schema-id param]]
    [(rf/subscribe [:resource.schema/output-structure schema-id param])
     (rf/subscribe [:resource.schema/output-data schema-id param])])
  (fn [[output-structure output-data]]
    (merge-with merge output-structure output-data)))

;; Handlers and subscriptions for dealing with the schema of a context source
;; parameter

(rf/reg-event-fx :resource.schema/get-parameter-schema
  [(rf/inject-cofx :sub :resource.user/token)
   (rf/inject-cofx :sub :url-context)]
  (fn [{:keys [url-context] :as cofx} [_ schema-id]]
    {:fx [[:http
           {:method :post
            :uri (url-tpl/render :api/schemas-parameter url-context)
            :params schema-id
            :headers {"Authorization" (:resource.user/token cofx)}
            :on-success [:resource.schema/get-parameter-schema-success schema-id]
            :on-failure [:resource.schema/get-parameter-schema-failure schema-id]}]]}))

(rf/reg-event-db :resource.schema/get-parameter-schema-success
  [(rf/path :resource.schema/parameter-schema)]
  (fn [schema->parameter-schema [_ schema output]]
    (assoc schema->parameter-schema schema output)))

(rf/reg-event-db :resource.schema/get-parameter-schema-failure
  [(rf/path :resource.schema/parameter-schema)]
  (fn [schema->parameter-schema [_ schema _error]]
    (dissoc schema->parameter-schema schema)))

(rf/reg-sub :resource.schema/parameter-schema
  (fn [db [_ schema-id]]
    (get-in db [:resource.schema/parameter-schema schema-id])))

(rf/reg-sub :resource.schema/schema-id->meta+data
  (fn [[_ schema-id param]]
    [(rf/subscribe [:resource.schema/meta-by-id schema-id])
     (rf/subscribe [:resource.schema/output schema-id param])])
  (fn [[meta output] _]
    (when meta
      {:meta meta :data {:out output}})))
